import useInView from '@propertypal/shared/src/hooks/useInView';
import { Property } from '@propertypal/shared/src/types/property';
import React, { FunctionComponent, useRef } from 'react';
import { Text } from '../typography';
import PropertyBox from './PropertyBox';
import PropertyPlaceholder from './PropertyPlaceholder';
import { Container, Content, LoadingBox, Rail, Title, SubTitle } from './SimilarProperties.style';

interface Props {
  properties?: Property[];
  onInView?: () => void;
  title?: string;
  subTitle?: string;
  emptyText?: string;
  background?: string;
  imageLocator?: string;
  onFavourite?: () => (favourited: boolean, propertyId: number) => Promise<boolean>;
  hideOnEmpty?: boolean;
}

const SimilarProperties: FunctionComponent<Props> = (props) => {
  const container = useRef<HTMLDivElement>(null);
  useInView(container, { threshold: 0.5, rootMargin: '0px 0px 800px' }, props.onInView);

  if (props.hideOnEmpty && props.properties?.length === 0) {
    return null;
  }

  const title = () =>
    !!props.title && (
      <Title className="pp-similar-properties-title">
        {props.title}{' '}
        {!!props.subTitle && <SubTitle className="pp-similar-properties-sub-title">{props.subTitle}</SubTitle>}
      </Title>
    );

  if (!props.properties || (props.properties.length === 0 && props.emptyText)) {
    return (
      <Container className="pp-similar-properties" ref={container} background={props.background}>
        <LoadingBox>
          {title()}

          {!props.properties && (
            <Rail>
              <PropertyPlaceholder hideFooter />
              <PropertyPlaceholder hideFooter />
              <PropertyPlaceholder hideFooter />
            </Rail>
          )}

          {props.properties?.length === 0 && props.emptyText && <Text mt={15}>{props.emptyText}</Text>}
        </LoadingBox>
      </Container>
    );
  }

  if (props.properties && props.properties.length > 0) {
    return (
      <Container className="pp-similar-properties" data-testid="similarProperties" background={props.background}>
        <Content>
          {title()}

          <Rail>
            {props.properties.map((property) => (
              <PropertyBox
                key={property.id}
                property={property}
                hideFooter
                imageLocator={props.imageLocator}
                onFavourite={props.onFavourite}
              />
            ))}
          </Rail>
        </Content>
      </Container>
    );
  }

  return null;
};

export default SimilarProperties;
